import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, AlertTriangle, Edit3, Save, XCircle, Maximize2, Minimize2 } from "lucide-react";
import ChatIcon from "../../components/Common/ChatIcon";
import { useUser } from "@clerk/clerk-react";
import { apiClient } from "../../config/api";
import API_CONFIG from "../../config/api";
import Overview from "./components/Overview";
import Indicators from "./components/Indicators";
import Endpoints from "./components/Endpoints";
import Timeline from "./components/Timeline";
import Response from "./components/Response";
import Attachments from "./components/Attachments";
import Chatbot from "./components/Chatbot";
import "./incident-detail.scss";

export default function IncidentDetailView({ incident, onClose, onUpdate, loading = false, error = null }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [showChat, setShowChat] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const { user } = useUser();
  const userClient = user?.publicMetadata?.client;

  // Initialize form data when incident changes
  useEffect(() => {
    if (incident) {
      // Handle both direct incident fields and _raw nested fields
      const helxonAssigned = incident.helxonAssignedTo || incident._raw?.helxonAssignedTo || null;
      const customerAssigned = incident.customerAssignedTo || incident._raw?.customerAssignedTo || null;
      
      setEditFormData({
        status: incident.status || incident.Status || "",
        severity: incident.severity || incident.Severity || "",
        helxonAssignedTo: helxonAssigned,
        customerAssignedTo: customerAssigned,
      });
    }
  }, [incident]);

  const handleEditFormChange = useCallback((field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = async () => {
    const incidentId =
      incident.incidentName ||
      incident.incidentId ||
      incident.incidentUUID ||
      incident.id;

    if (!incidentId) {
      setSaveError("Unable to identify incident for update");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Build update payload - only include fields that have values
      const updatePayload = {};
      
      if (editFormData.status) {
        updatePayload.status = editFormData.status;
      }
      
      if (editFormData.severity) {
        updatePayload.severity = editFormData.severity;
      }
      
      // For assignees, explicitly include null to unassign or the user object
      updatePayload.helxonAssignedTo = editFormData.helxonAssignedTo || null;
      updatePayload.customerAssignedTo = editFormData.customerAssignedTo || null;

      const response = await apiClient.put(
        API_CONFIG.ENDPOINTS.INCIDENT_UPDATE(incidentId),
        updatePayload
      );

      setIsEditing(false);

      // Notify parent to refresh incident data
      // The response contains { success, message, data }
      if (onUpdate && response.data) {
        const updatedData = response.data.data || response.data;
        onUpdate(updatedData);
      }
    } catch (err) {
      console.error("Failed to update incident:", err);
      setSaveError(err.response?.data?.message || "Failed to update incident");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to original values
    const helxonAssigned = incident.helxonAssignedTo || incident._raw?.helxonAssignedTo || null;
    const customerAssigned = incident.customerAssignedTo || incident._raw?.customerAssignedTo || null;
    
    setEditFormData({
      status: incident.status || incident.Status || "",
      severity: incident.severity || incident.Severity || "",
      helxonAssignedTo: helxonAssigned,
      customerAssignedTo: customerAssigned,
    });
    setIsEditing(false);
    setSaveError(null);
  };

  // Add blur effect to header and sidebar when modal opens
  useEffect(() => {
    document.body.classList.add("modal-open");
    
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "indicators", label: "Indicators" },
    { id: "endpoints", label: "Endpoints" },
    { id: "timeline", label: "Timeline" },
    { id: "response", label: "Response" },
    { id: "attachments", label: "Attachments" },
  ];

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "Critical":
        return "badge badge--critical";
      case "High":
        return "badge badge--high";
      case "Medium":
        return "badge badge--medium";
      case "Low":
        return "badge badge--low";
      default:
        return "badge";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Open":
        return "chip chip--danger";
      case "In Progress":
        return "chip chip--warn";
      case "Resolved":
        return "chip chip--success";
      case "Closed":
        return "chip";
      default:
        return "chip";
    }
  };

  const handleEscalate = async () => {
    if (!incident) return;
    setEscalating(true);
    setEscalateError(null);
    try {
      const id =
        incident.incidentId ||
        incident.incidentUUID ||
        incident.id ||
        incident.incidentNumber ||
        incident.incidentName;
      const rulesResponse = await apiClient.get(API_CONFIG.ENDPOINTS.ALERT_RULES);
      const rulesData = (() => {
        const d = rulesResponse.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.data)) return d.data;
        if (Array.isArray(d?.data?.rules)) return d.data.rules;
        return [];
      })();
      const filteredRules = rulesData;

      const extractEntities = (alertsArr = []) => {
        return alertsArr
          .map((alert) => {
            let ents = alert.entities || alert.Entities || [];
            if (typeof ents === "string") {
              try {
                ents = JSON.parse(ents);
              } catch {
                ents = [];
              }
            }
            if (!Array.isArray(ents)) ents = [];
            return {
              alertId: alert.id || alert.systemAlertId || alert.alertId || null,
              alertName: alert.displayName || alert.alertName || alert.name || null,
              severity: alert.alertSeverity || alert.severity || null,
              sourceSystem: alert.sourceSystem || alert.SourceSystem || null,
              entities: ents,
            };
          })
          .filter((a) => a.entities.length);
      };

      const alertsFromIncident =
        incident.alerts ||
        incident._raw?.alerts ||
        incident.Alerts ||
        incident._raw?.Alerts ||
        [];
      const alertEntities = extractEntities(alertsFromIncident);

      const extractAlertWorkspaces = (alertsArr = []) => {
        const workspaceMap = new Map();
        alertsArr.forEach((alert) => {
          const wsId =
            alert.workspaceId ||
            alert.workspaceID ||
            alert.workspace_id ||
            alert.WorkspaceId ||
            alert.WorkspaceID ||
            alert.Workspace_id ||
            alert.workspace?.id ||
            alert.workspace?.workspaceId ||
            null;
          const wsName =
            alert.workspaceName ||
            alert.workspace_name ||
            alert.WorkspaceName ||
            alert.workspace?.name ||
            alert.workspace?.workspaceName ||
            null;
          const key = wsId || wsName;
          if (key && !workspaceMap.has(key)) {
            workspaceMap.set(key, { id: wsId || null, name: wsName || null });
          }
        });
        return Array.from(workspaceMap.values());
      };

      const alertWorkspaces = extractAlertWorkspaces(alertsFromIncident);

      const sanitizedIncident = (() => {
        const copy = { ...incident };
        if (copy.alerts) delete copy.alerts;
        if (copy._raw?.alerts) {
          copy._raw = { ...copy._raw };
          delete copy._raw.alerts;
        }
        return copy;
      })();

      await apiClient.post(API_CONFIG.ENDPOINTS.LOGIC_APP_TRIGGER, {
        incident: sanitizedIncident,
        rules: filteredRules,
        alertEntities,
        alertWorkspaces,
      });
    } catch (err) {
      setEscalateError(err.response?.data?.message || err.message || "Failed to escalate incident");
    } finally {
      setEscalating(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <Overview
            incident={incident}
            isEditing={isEditing}
            editFormData={editFormData}
            onFormChange={handleEditFormChange}
          />
        );
      case "indicators":
        return <Indicators incident={incident} />;
      case "endpoints":
        return <Endpoints incident={incident} />;
      case "timeline":
        return <Timeline incident={incident} />;
      case "response":
        return <Response incident={incident} />;
      case "attachments":
        return <Attachments incident={incident} />;
      default:
        return (
          <Overview
            incident={incident}
            isEditing={isEditing}
            editFormData={editFormData}
            onFormChange={handleEditFormChange}
          />
        );
    }
  };

  return createPortal(
    <div className={`incident-detail-overlay${isExpanded ? " incident-detail-overlay--expanded" : ""}`}>
      <div className={`incident-detail-modal${isExpanded ? " incident-detail-modal--expanded" : ""}`}>
        {/* Header */}
        <div className="incident-detail__header">
          <div className="incident-detail__title-section">
            <div className="incident-detail__id-section">
              <span className="incident-detail__id">INC-{incident.id}</span>
              <span className="incident-detail__divider"></span>
              <span className={getSeverityClass(incident.severity)}>
                {incident.severity}
              </span>
              <span className="incident-detail__divider"></span>
              <h2 className="incident-detail__title">{incident.title}</h2>
              {/* <span className={getStatusClass(incident.status)}>
                {incident.status}
              </span> */}
            </div>
            
            <p className="incident-detail__description">
              {incident.description}
            </p>
          </div>
          <div className="incident-detail__actions">
            {escalateError && (
              <div className="incident-detail__notice chip chip--danger">
                {escalateError}
              </div>
            )}
            {saveError && (
              <div className="incident-detail__notice chip chip--danger">
                {saveError}
              </div>
            )}
            {isEditing ? (
              <>
                <button
                  className="btn btn--primary incident-detail__save"
                  onClick={handleSave}
                  disabled={isSaving}
                  title="Save Changes"
                >
                  <Save size={16} />
                  {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                  className="btn btn--outline incident-detail__cancel"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  title="Cancel Edit"
                >
                  <XCircle size={16} />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn--outline incident-detail__edit"
                  onClick={() => setIsEditing(true)}
                  title="Edit Incident"
                >
                  <Edit3 size={16} />
                  Edit
                </button>
                {userClient?.name === "Helxon" && (
                  <button
                    className="btn btn--danger incident-detail__escalate"
                    onClick={handleEscalate}
                    disabled={escalating}
                    title="Escalate Incident"
                  >
                    <AlertTriangle size={16} />
                    {escalating ? "Escalating..." : "Escalate"}
                  </button>
                )}
              </>
            )}
            <button
              className="incident-detail__expand"
              onClick={() => setIsExpanded((prev) => !prev)}
              title={isExpanded ? "Minimize" : "Expand"}
            >
              {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button className="incident-detail__close" onClick={onClose} title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="incident-detail__tabs-wrapper">
          <div className="incident-detail__tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`incident-detail__tab ${
                  activeTab === tab.id ? "is-active" : ""
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="incident-detail__chat-toggle ">
            <button 
              className={`incident-detail__chat-toggle ${!showChat ? "incident-detail__chat-toggle--collapsed" : ""}`}
              onClick={() => setShowChat(!showChat)}
              title={showChat ? "Hide chat" : "Show chat"}
            >
              <ChatIcon size={18} />
            </button>
          </div>
        </div>

        {loading && (
          <div className="incident-detail__notice chip chip--muted">
            Loading latest incident details…
          </div>
        )}
        {error && (
          <div className="incident-detail__notice chip chip--danger">
            {error}
          </div>
        )}

        {/* Content */}
        <div className={`incident-detail__content-wrapper ${!showChat ? "incident-detail__content-wrapper--full" : ""}`}>
          <div className="incident-detail__content">{renderTabContent()}</div>
          {showChat && (
            <div className="incident-detail__sidebar">
              <Chatbot incident={incident} />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
